// src/pages/CollectPayment.tsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getVehicleById,
  VehicleData,
  createPayment,
  getPaymentsByVehicle,
} from "../services/api";

interface Payment {
  id: number;
  amount: number;
  notes: string | null;
  paymentDate: string;
}

const CollectPayment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState<VehicleData | null>(null);
  const [amount, setAmount] = useState<number | "">("");
  const [notes, setNotes] = useState("");
  const [lastPayments, setLastPayments] = useState<Payment[]>([]);

  const fetchPayments = useCallback(async () => {
    const vehicleIdNum = Number(id);
    let data = await getPaymentsByVehicle(vehicleIdNum);
    data.sort(
      (a: Payment, b: Payment) =>
        new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
    );
    setLastPayments(data.slice(0, 5));
  }, [id]);

  const fetchVehicle = useCallback(async () => {
    const data = await getVehicleById(id!);
    setVehicle(data);
  }, [id]);

  useEffect(() => {
    fetchPayments();
    fetchVehicle();
  }, [fetchPayments, fetchVehicle]);

  const handlePayment = async () => {
    if (!vehicle) return;
    if (!amount || amount <= 0) return alert("Enter a valid amount");
    if (
      !window.confirm(
        `Confirm payment of ₹${amount} for vehicle ${vehicle.vehicleNo}?`
      )
    )
      return;

    try {
      await createPayment({
        vehicleId: vehicle.id,
        amount: Number(amount),
        notes,
      });
      alert("Payment collected successfully!");

      // Reset input fields
      setAmount("");
      setNotes("");

      // Refresh last payments and vehicle details
      fetchPayments();
      fetchVehicle();
    } catch (err) {
      console.error(err);
      alert("Error collecting payment");
    }
  };

  if (!vehicle) return <div className="p-6">Loading...</div>;

  return (
    <div className="min-h-screen w-full bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="flex items-center bg-white shadow p-4">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 transition text-sm md:text-base"
        >
          Back
        </button>
        <h1 className="ml-4 text-xl md:text-2xl font-bold text-gray-800">
          Collect Payment
        </h1>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-6 space-y-6">
        {/* Vehicle Details */}
        <div className="bg-white shadow rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <p>
            <span className="font-semibold text-gray-600">Vehicle No:</span>{" "}
            {vehicle.vehicleNo}
          </p>
          <p>
            <span className="font-semibold text-gray-600">Owner:</span>{" "}
            {vehicle.ownerName}
          </p>
          <p>
            <span className="font-semibold text-gray-600">Type:</span>{" "}
            {vehicle.vehicleType}
          </p>
          <p>
            <span className="font-semibold text-gray-600">Balance:</span> ₹
            {vehicle.balance}
          </p>
        </div>

        {/* Payment Form */}
        <div className="bg-white shadow rounded-lg p-6 w-full">
          <h2 className="text-lg font-semibold mb-4">Enter Payment</h2>
          <div className="flex flex-col md:flex-row md:space-x-4 space-y-3 md:space-y-0 w-full">
            <input
              type="number"
              placeholder="Payment Amount"
              value={amount}
              onChange={(e) =>
                setAmount(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="border p-3 rounded w-full flex-1"
            />
            <input
              type="text"
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="border p-3 rounded w-full flex-1"
            />
          </div>
          <button
            className="bg-green-600 text-white px-4 py-3 rounded w-full mt-4 hover:bg-green-700 transition"
            onClick={handlePayment}
          >
            Confirm Payment
          </button>
        </div>

        {/* Last Payments */}
        <div>
          <h2 className="text-xl font-semibold mb-3">Last 5 Payments</h2>
          {lastPayments.length === 0 ? (
            <p className="text-gray-500">No previous payments found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white shadow rounded">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-4 text-left">ID</th>
                    <th className="py-2 px-4 text-left">Amount (₹)</th>
                    <th className="py-2 px-4 text-left">Notes</th>
                    <th className="py-2 px-4 text-left">Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {lastPayments.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b hover:bg-blue-50 transition"
                    >
                      <td className="py-2 px-4">{p.id}</td>
                      <td className="py-2 px-4">₹{p.amount}</td>
                      <td className="py-2 px-4">{p.notes || "-"}</td>
                      <td className="py-2 px-4">
                        {new Date(p.paymentDate).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectPayment;
